import { siteApi } from "@api";
import { Paths } from "@constants";
import logger from "@utils/logger";
import { depositWindowStore } from "@utils/window-handler-store";
import { SecretCodeState } from "@views/PromotionSecretCodePage/types";
import { ReferralBonusTokenModel, WalletTxMethod } from "kz-ui-sdk";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getTakerState } from "./useSecretCodeStatus";
import useUserMode from "./useUserMode";

// Constants
const PROMOTION_STATUS_REFETCH_DELAY_MS = 1000;
const POLLING_INTERVAL_MS = 1000;
const POLLING_DURATION_MS = 10000;
const MAX_POLLS = 10;
const REFERRAL_TOKEN_QUERY_LIMIT = 100_000;

const REFERRAL_TOKEN_QUERY_PARAMS = {
  redeemedAt: "null",
  offset: 0,
  limit: REFERRAL_TOKEN_QUERY_LIMIT,
} as const;

// Types
interface RefetchStrategy {
  refetchReward: boolean;
  refetchReferral: boolean;
}

interface TokenData {
  entries?: ReferralBonusTokenModel[];
}

interface RefetchResult {
  rewardData?: ReturnType<typeof siteApi.useGetRewardsStatusQuery>["data"];
  referralData?: ReturnType<typeof siteApi.useGetReferralRewardStatusQuery>["data"];
  tokenData?: TokenData;
}

interface PromotionConfig {
  refetchReward: boolean;
  refetchReferral: boolean;
  needsSecondPhase: boolean;
  checkInitialEligibility: (data: {
    rewardData?: RefetchResult["rewardData"];
    referralData?: RefetchResult["referralData"];
  }) => boolean;
  checkComplete: (data: RefetchResult) => boolean;
  checkFullEligibility: (data: RefetchResult) => boolean;
}

/**
 * Hook to handle post-deposit completion actions (refetching promotion statuses)
 * Can be reused across different deposit success flows
 *
 * @returns Object with onComplete callback to be called when deposit is successful
 */
export function useDepositCompleteHandler() {
  const location = useLocation();
  const { isGuest } = useUserMode();
  const [refetchRewardStatus] = siteApi.useLazyGetRewardsStatusQuery();
  const [refetchReferralRewardStatus] = siteApi.useLazyGetReferralRewardStatusQuery();
  const [refetchReferralRewardTokens] = siteApi.useLazyGetReferralRewardTokensQuery();

  // Use regular queries to read current status for eligibility checks
  const { data: rewardStatusRawData } = siteApi.useGetRewardsStatusQuery(undefined, {
    skip: isGuest,
  });
  const { data: referralRewardStatusData } = siteApi.useGetReferralRewardStatusQuery(undefined, {
    skip: isGuest,
  });

  const initialTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper functions for eligibility checks
  const checkDailyBonusEligibility = useCallback((rewardData?: RefetchResult["rewardData"]) => {
    const dailyBonusStatus = rewardData?.[WalletTxMethod.DailyBonus];
    return !!dailyBonusStatus;
  }, []);

  const checkPlaySpinEligibility = useCallback((rewardData?: RefetchResult["rewardData"]) => {
    const playSpinStatus = rewardData?.[WalletTxMethod.PlaySpin];
    return !(!!playSpinStatus?.enabled && !!playSpinStatus?.lockedInstructions);
  }, []);

  const checkPlaySpinComplete = useCallback((rewardData?: RefetchResult["rewardData"]) => {
    const playSpinStatus = rewardData?.[WalletTxMethod.PlaySpin];
    return !!playSpinStatus?.lastPlaySpinTx;
  }, []);

  const checkReferralRewardEligibility = useCallback((referralData?: RefetchResult["referralData"]) => {
    return !!(referralData?.enabled && referralData?.canParticipate);
  }, []);

  const checkReferralTokensComplete = useCallback((tokenData?: TokenData) => {
    return (tokenData?.entries?.length ?? 0) > 0;
  }, []);

  const checkSecretCodeEligibility = useCallback((rewardData?: RefetchResult["rewardData"]) => {
    const secretCodeStatus = rewardData?.[WalletTxMethod.SecretCode];
    const secretCodeTakerStateData = getTakerState(secretCodeStatus);
    return secretCodeTakerStateData.state !== SecretCodeState.LOCKED;
  }, []);

  // Promotion configurations
  const getPromotionConfig = useCallback(
    (pathname: string): PromotionConfig | null => {
      if (pathname === Paths.PRIVATE.PRIZES) {
        return {
          refetchReward: true,
          refetchReferral: true,
          needsSecondPhase: false,
          checkInitialEligibility: ({ rewardData }) => {
            return checkDailyBonusEligibility(rewardData) && checkPlaySpinEligibility(rewardData);
          },
          checkComplete: () => false,
          checkFullEligibility: ({ rewardData }) => {
            return checkDailyBonusEligibility(rewardData) && checkPlaySpinEligibility(rewardData);
          },
        };
      }

      if (pathname === Paths.PRIVATE.PROMOTION_DAILY_LOGIN) {
        return {
          refetchReward: true,
          refetchReferral: false,
          needsSecondPhase: false,
          checkInitialEligibility: ({ rewardData }) => checkDailyBonusEligibility(rewardData),
          checkComplete: () => false,
          checkFullEligibility: ({ rewardData }) => checkDailyBonusEligibility(rewardData),
        };
      }

      if (pathname === Paths.PRIVATE.PROMOTION_PLAY_AND_SPIN) {
        return {
          refetchReward: true,
          refetchReferral: false,
          needsSecondPhase: true,
          checkInitialEligibility: ({ rewardData }) => checkPlaySpinEligibility(rewardData),
          checkComplete: ({ rewardData }) => checkPlaySpinComplete(rewardData),
          checkFullEligibility: ({ rewardData }) => {
            return checkPlaySpinEligibility(rewardData) && checkPlaySpinComplete(rewardData);
          },
        };
      }

      if (pathname === Paths.PRIVATE.PROMOTION_REFER_BALLS) {
        return {
          refetchReward: false,
          refetchReferral: true,
          needsSecondPhase: true,
          checkInitialEligibility: ({ referralData }) => checkReferralRewardEligibility(referralData),
          checkComplete: ({ tokenData }) => checkReferralTokensComplete(tokenData),
          checkFullEligibility: ({ referralData, tokenData }) => {
            return checkReferralRewardEligibility(referralData) && checkReferralTokensComplete(tokenData);
          },
        };
      }

      if (pathname === Paths.PRIVATE.PROMOTION_REDEEM_SECRET_CODE) {
        return {
          refetchReward: true,
          refetchReferral: false,
          needsSecondPhase: false,
          checkInitialEligibility: ({ rewardData }) => checkSecretCodeEligibility(rewardData),
          checkComplete: () => false,
          checkFullEligibility: ({ rewardData }) => checkSecretCodeEligibility(rewardData),
        };
      }

      return null;
    },
    [
      checkDailyBonusEligibility,
      checkPlaySpinEligibility,
      checkPlaySpinComplete,
      checkReferralRewardEligibility,
      checkReferralTokensComplete,
      checkSecretCodeEligibility,
    ],
  );

  const getRefetchStrategy = useCallback((): RefetchStrategy => {
    const config = getPromotionConfig(location.pathname);
    if (!config) {
      return { refetchReward: false, refetchReferral: false };
    }
    return {
      refetchReward: config.refetchReward,
      refetchReferral: config.refetchReferral,
    };
  }, [location.pathname, getPromotionConfig]);

  const performRefetch = useCallback(
    async (strategy: RefetchStrategy, includeTokens = false): Promise<RefetchResult> => {
      const promises: Promise<unknown>[] = [];
      let rewardPromise: Promise<{ data?: RefetchResult["rewardData"] }> | null = null;
      let referralPromise: Promise<{ data?: RefetchResult["referralData"] }> | null = null;
      let tokenPromise: Promise<{ data?: TokenData }> | null = null;

      try {
        if (strategy.refetchReward) {
          const result = refetchRewardStatus();
          if (result) {
            promises.push(result);
            rewardPromise = result as Promise<{ data?: RefetchResult["rewardData"] }>;
          }
        }
        if (strategy.refetchReferral) {
          const result = refetchReferralRewardStatus();
          if (result) {
            promises.push(result);
            referralPromise = result as Promise<{ data?: RefetchResult["referralData"] }>;
          }
        }
        if (includeTokens && strategy.refetchReferral) {
          const result = refetchReferralRewardTokens({
            query: REFERRAL_TOKEN_QUERY_PARAMS,
          });
          if (result) {
            promises.push(result);
            tokenPromise = result as Promise<{ data?: TokenData }>;
          }
        }

        await Promise.allSettled(promises);

        const rewardResult = rewardPromise ? await rewardPromise : undefined;
        const referralResult = referralPromise ? await referralPromise : undefined;
        const tokenResult = tokenPromise ? await tokenPromise : undefined;

        return {
          rewardData: rewardResult?.data,
          referralData: referralResult?.data,
          tokenData: tokenResult?.data,
        };
      } catch (error) {
        logger._console.error("Failed to refetch promotion statuses:", error);
        return {
          rewardData: undefined,
          referralData: undefined,
          tokenData: undefined,
        };
      }
    },
    [refetchRewardStatus, refetchReferralRewardStatus, refetchReferralRewardTokens],
  );

  const clearAllTimers = useCallback(() => {
    if (initialTimeoutRef.current) {
      clearTimeout(initialTimeoutRef.current);
      initialTimeoutRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  const startPhaseOnePolling = useCallback(
    (strategy: RefetchStrategy, config: PromotionConfig, onEligible: () => void) => {
      let pollCount = 0;

      const intervalId = setInterval(async () => {
        // Check if interval was cleared before executing
        if (pollingIntervalRef.current !== intervalId) {
          return;
        }

        pollCount++;
        const pollResult = await performRefetch(strategy);

        // Check again after async operation
        if (pollingIntervalRef.current !== intervalId) {
          return;
        }

        const isEligible = config.checkInitialEligibility({
          rewardData: pollResult.rewardData,
          referralData: pollResult.referralData,
        });

        if (isEligible || pollCount >= MAX_POLLS) {
          if (pollingIntervalRef.current === intervalId) {
            clearInterval(intervalId);
            pollingIntervalRef.current = null;
          }
          if (isEligible) {
            onEligible();
          }
        }
      }, POLLING_INTERVAL_MS);

      pollingIntervalRef.current = intervalId;
    },
    [performRefetch],
  );

  const startSecondPhasePolling = useCallback(
    (strategy: RefetchStrategy, config: PromotionConfig) => {
      if (!config.needsSecondPhase) {
        return;
      }

      // Clear any existing interval before starting new one
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      let pollCount = 0;
      const startTime = Date.now();
      const includeTokens = config.refetchReferral;

      const intervalId = setInterval(async () => {
        // Check if interval was cleared before executing
        if (pollingIntervalRef.current !== intervalId) {
          return;
        }

        pollCount++;
        const refetchResult = await performRefetch(strategy, includeTokens);

        // Check again after async operation
        if (pollingIntervalRef.current !== intervalId) {
          return;
        }

        const isComplete = config.checkFullEligibility(refetchResult);

        const elapsed = Date.now() - startTime;
        if (isComplete || pollCount >= MAX_POLLS || elapsed >= POLLING_DURATION_MS) {
          if (pollingIntervalRef.current === intervalId) {
            clearInterval(intervalId);
            pollingIntervalRef.current = null;
          }
          if (isComplete) {
            clearAllTimers();
          }
        }
      }, POLLING_INTERVAL_MS);

      pollingIntervalRef.current = intervalId;
    },
    [performRefetch, clearAllTimers],
  );

  const setupSafetyTimeout = useCallback(() => {
    pollingTimeoutRef.current = setTimeout(() => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      pollingTimeoutRef.current = null;
    }, POLLING_DURATION_MS);
  }, []);

  const handleInitialRefetch = useCallback(
    async (strategy: RefetchStrategy, config: PromotionConfig) => {
      const includeTokens = config.refetchReferral && config.needsSecondPhase;
      const refetchResult = await performRefetch(strategy, includeTokens);

      const initialEligible = config.checkInitialEligibility({
        rewardData: refetchResult.rewardData,
        referralData: refetchResult.referralData,
      });

      const alreadyComplete = config.checkComplete(refetchResult);

      if (alreadyComplete) {
        clearAllTimers();
        return;
      }

      if (!initialEligible) {
        startPhaseOnePolling(strategy, config, () => {
          startSecondPhasePolling(strategy, config);
        });
      } else {
        startSecondPhasePolling(strategy, config);
      }

      setupSafetyTimeout();
    },
    [performRefetch, clearAllTimers, startPhaseOnePolling, startSecondPhasePolling, setupSafetyTimeout],
  );

  const shouldSkipPolling = useCallback(
    (strategy: RefetchStrategy, config: PromotionConfig | null): boolean => {
      if (!config) {
        return true;
      }

      if (!strategy.refetchReward && !strategy.refetchReferral) {
        return true;
      }

      // Check if already complete (for second phase promotions)
      if (config.needsSecondPhase) {
        if (location.pathname === Paths.PRIVATE.PROMOTION_PLAY_AND_SPIN) {
          if (checkPlaySpinComplete(rewardStatusRawData)) {
            return true;
          }
        }
        // For ReferBalls, we can't check tokens without refetching, so we'll check in handleInitialRefetch
      }

      // For non-second-phase promotions, check initial eligibility
      if (!config.needsSecondPhase) {
        const initialEligible = config.checkInitialEligibility({
          rewardData: rewardStatusRawData,
          referralData: referralRewardStatusData,
        });
        if (initialEligible) {
          return true;
        }
      }

      return false;
    },
    [location.pathname, rewardStatusRawData, referralRewardStatusData, checkPlaySpinComplete],
  );

  const onComplete = useCallback(() => {
    // Close deposit window if it exists
    depositWindowStore.close();

    // Clear any existing timers to prevent multiple pending refetches
    clearAllTimers();

    const strategy = getRefetchStrategy();
    const config = getPromotionConfig(location.pathname);

    if (shouldSkipPolling(strategy, config)) {
      return;
    }

    if (!config) {
      return;
    }

    initialTimeoutRef.current = setTimeout(() => {
      handleInitialRefetch(strategy, config);
      initialTimeoutRef.current = null;
    }, PROMOTION_STATUS_REFETCH_DELAY_MS);
  }, [
    clearAllTimers,
    getRefetchStrategy,
    getPromotionConfig,
    location.pathname,
    shouldSkipPolling,
    handleInitialRefetch,
  ]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  return {
    onComplete,
  };
}
